import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';

function Home() {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Calculate zoom effect based on scroll
  const zoomScale = 1 + (scrollY * 0.0003); // Subtle zoom from 1.0 to ~1.3

  return (
    <div className="relative">
      {/* Hero Section - Stark White with Arctic Image */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-white">
        {/* Arctic Background Image with Zoom Effect */}
        <div
          className="absolute inset-0 opacity-40"
          style={{
            transform: `scale(${zoomScale})`,
            transition: 'transform 0.1s ease-out',
          }}
        >
          <img
            src="/arctic-station.png"
            alt="Arctic Research Station"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Subtle gradient overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-white/40 to-white/20"></div>

        {/* Hero Content */}
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-6xl md:text-8xl font-mono font-light mb-8 text-gray-900 tracking-tight">
              VELUMCLOUD
            </h1>
            <p className="text-xl md:text-2xl font-mono font-light text-gray-700 mb-12 tracking-wide leading-relaxed">
              SECURE YOUR CLOUD<br />
              WITH CONFIDENCE
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link 
                to="/contact" 
                className="font-mono px-8 py-4 bg-black text-white hover:bg-gray-800 transition-colors text-sm tracking-widest border border-black"
              >
                GET STARTED
            </Link>
              <Link 
                to="/vpn" 
                className="font-mono px-8 py-4 bg-transparent text-black border border-black hover:bg-black hover:text-white transition-colors text-sm tracking-widest"
              >
                EXPLORE PRODUCTS
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </section>

      {/* Transition Zone - Cards floating above */}
      <section className="relative bg-gradient-to-b from-white via-gray-100 to-cyber-darker py-32">
        <div className="container mx-auto px-4 relative z-20">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-mono font-light mb-6 text-gray-900">
              WHY VELUMCLOUD
            </h2>
            <p className="text-lg font-mono font-light text-gray-700 tracking-wide">
              ENTERPRISE SECURITY WITHOUT COMPLEXITY
            </p>
          </div>

          {/* Feature Cards with elevation */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
            {/* Feature 1 */}
            <div className="bg-white/90 backdrop-blur-sm border border-gray-300 p-8 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
              <div className="w-12 h-12 border border-gray-900 flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-xl font-mono font-normal mb-4 text-gray-900 tracking-wide">ZERO-TRUST</h3>
              <p className="text-sm font-mono font-light text-gray-600 leading-relaxed tracking-wide">
                Never trust, always verify. Our zero-trust architecture ensures every access request is authenticated.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white/90 backdrop-blur-sm border border-gray-300 p-8 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
              <div className="w-12 h-12 border border-gray-900 flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-mono font-normal mb-4 text-gray-900 tracking-wide">LIGHTNING FAST</h3>
              <p className="text-sm font-mono font-light text-gray-600 leading-relaxed tracking-wide">
                Global infrastructure with sub-50ms latency. Security that doesn't slow you down.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white/90 backdrop-blur-sm border border-gray-300 p-8 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
              <div className="w-12 h-12 border border-gray-900 flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-mono font-normal mb-4 text-gray-900 tracking-wide">COMPLIANCE</h3>
              <p className="text-sm font-mono font-light text-gray-600 leading-relaxed tracking-wide">
                SOC 2, GDPR, HIPAA compliant out of the box. Meet your regulatory requirements.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Dark Cyber Section - Products */}
      <section className="py-20 bg-cyber-darker relative">
        {/* Subtle grid background */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'linear-gradient(rgba(0, 212, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 212, 255, 0.1) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="gradient-text">Our Solutions</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* VPN Card */}
            <Link to="/vpn" className="card group hover:scale-105 transition-transform">
              <div className="mb-4">
                <span className="inline-block px-3 py-1 bg-cyber-blue/20 text-cyber-blue rounded-full text-sm font-semibold font-mono tracking-wider">
                  VPN
                </span>
              </div>
              <h3 className="text-3xl font-bold mb-4 text-white">Enterprise VPN</h3>
              <p className="text-gray-400 mb-6">
                Secure remote access with military-grade encryption. Connect your distributed workforce safely.
              </p>
              <div className="flex items-center text-cyber-blue group-hover:translate-x-2 transition-transform">
                <span className="font-semibold">Learn More</span>
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </div>
            </Link>

            {/* CASB Card */}
            <Link to="/casb" className="card group hover:scale-105 transition-transform">
              <div className="mb-4">
                <span className="inline-block px-3 py-1 bg-cyber-purple/20 text-cyber-purple rounded-full text-sm font-semibold font-mono tracking-wider">
                  CASB
                </span>
              </div>
              <h3 className="text-3xl font-bold mb-4 text-white">Cloud Security</h3>
              <p className="text-gray-400 mb-6">
                Comprehensive CASB platform for SaaS visibility, data protection, and threat prevention.
              </p>
              <div className="flex items-center text-cyber-purple group-hover:translate-x-2 transition-transform">
                <span className="font-semibold">Learn More</span>
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden bg-cyber-darker">
        <div className="absolute inset-0 bg-gradient-to-r from-cyber-blue/10 via-cyber-purple/10 to-cyber-pink/10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to <span className="gradient-text">Secure Your Future?</span>
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Join hundreds of enterprises trusting Velumcloud for their security needs.
            </p>
            <Link to="/contact" className="btn-primary text-lg">
              Contact Sales
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
